import React, { useMemo } from 'react';
import CodeEditor from '@uiw/react-textarea-code-editor';
import { CopyButton } from '@/components/copy-button';
import { UrlItem } from './url-list-item';
import { Skeleton } from '@/components/ui/skeleton';
import { useTheme } from 'next-themes';

interface MarkdownViewerProps {
  // 两种模式之一：直接传入 markdown 内容
  markdown?: string;
  title?: string;
  // 或者传入 selectedItem (用于 batch-url-md)
  selectedItem?: UrlItem | null;
  // 容器样式
  containerClassName?: string;
  copyButtonClassName?: string;
  noSelectionMessage?: string;
  loadingMessage?: string;
  errorMessage?: string;
  notParsedMessage?: string;
}

export const MarkdownViewer: React.FC<MarkdownViewerProps> = ({
  markdown,
  title = '预览 Markdown',
  selectedItem = null,
  containerClassName = "h-full flex flex-col",
  copyButtonClassName = "absolute top-2 right-2 z-10",
  noSelectionMessage = "请选择 URL 查看 Markdown 内容",
  loadingMessage = "解析中，请稍候...",
  errorMessage = "发生错误",
  notParsedMessage = "尚未解析，请点击开始解析按钮"
}) => {
  const { resolvedTheme } = useTheme();
  const colorMode = useMemo(() => 
    resolvedTheme === 'dark' ? 'dark' : 'light', 
    [resolvedTheme]
  );

  // 确定要显示的内容
  let contentToDisplay: string | null = null;
  let displayTitle = title;
  let isLoading = false;
  let error: string | null = null;

  // 根据不同的输入模式处理
  if (markdown !== undefined) {
    contentToDisplay = markdown;
  } else if (selectedItem) {
    contentToDisplay = selectedItem.markdown;
    displayTitle = selectedItem.title || selectedItem.url || title;
    isLoading = selectedItem.loading;
    error = selectedItem.error;
  }

  return (
    <div className={containerClassName}>
      <div className="flex-grow h-full flex flex-col overflow-hidden">
        {!selectedItem && markdown === undefined ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500 dark:text-gray-400 text-center px-6 py-20">
              {noSelectionMessage}
            </div>
          </div>
        ) : isLoading ? (
          <div className="flex flex-col items-center justify-center h-full space-y-4">
            <Skeleton className="w-2/3 h-4" />
            <Skeleton className="w-1/2 h-4" />
            <Skeleton className="w-3/4 h-4" />
            <div className="text-blue-500 dark:text-blue-400 text-center mt-2">
              {loadingMessage}
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-red-500 dark:text-red-400 text-center px-6 py-20">
              {error || errorMessage}
            </div>
          </div>
        ) : !contentToDisplay ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-500 dark:text-gray-400 text-center px-6 py-20">
              {notParsedMessage}
            </div>
          </div>
        ) : (
          <div className="w-full h-full overflow-auto">
            <div className="min-h-full">
              <CodeEditor
                value={contentToDisplay || ''}
                language="markdown"
                placeholder="请输入 Markdown 内容"
                padding={16}
                readOnly={true}
                style={{
                  fontSize: 14,
                  backgroundColor: "transparent",
                  fontFamily: 'ui-monospace, SFMono-Regular, SF Mono, Menlo, Consolas, Liberation Mono, monospace',
                  width: '100%',
                }}
                data-color-mode={colorMode}
                className="min-h-full"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 